import { useState, useCallback } from 'react';

/**
 * Hook para gerenciar notificações snackbar
 * Padroniza exibição de mensagens de sucesso/erro
 * 
 * @returns {Object} Estado do snackbar e handlers
 * 
 * @example
 * const { showSuccess, showError, snackbar, hideSnackbar } = useSnackbar();
 * 
 * try {
 *   await api.create(data);
 *   showSuccess('Item criado com sucesso!');
 * } catch (error) {
 *   showError('Erro ao criar item');
 * }
 * 
 * <Snackbar 
 *   open={snackbar.open}
 *   message={snackbar.message}
 *   onClose={hideSnackbar}
 *   autoHideDuration={6000}
 * >
 *   <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
 * </Snackbar>
 */
export const useSnackbar = () => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success', // 'success' | 'error' | 'warning' | 'info'
  });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  const showSuccess = useCallback((message) => {
    showSnackbar(message, 'success');
  }, [showSnackbar]);

  const showError = useCallback((message) => {
    showSnackbar(message, 'error');
  }, [showSnackbar]);

  const showWarning = useCallback((message) => {
    showSnackbar(message, 'warning');
  }, [showSnackbar]);

  const showInfo = useCallback((message) => {
    showSnackbar(message, 'info');
  }, [showSnackbar]);

  const hideSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  return {
    snackbar,
    showSnackbar,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hideSnackbar,
  };
};

export default useSnackbar;
